import { useEffect, useState } from 'react'
import { useFlag } from '@openfeature/react-sdk'

import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchAllocationRulesIfNeeded } from '@/actions/allocationRuleActions'
import { getCurrentUser } from '@/selectors/user.selectors'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'

import { fetchFinancialProfileActionItems } from './financialProfileActionItems.slice'
import { fetchUserActionItemsIfNeeded } from './user-action-items/lib/userActionItems.slice'
import { fetchSCorpElectionStatus } from './s-corp-upsell/sCorpActions'

import { MaximizeTaxDeductionsCard } from '@/features/user/dashboard/insights-cards/MaximizeTaxDeductionsCard'
import { NewOnHomepageBanner } from '@/features/user/dashboard/NewOnHomepageBanner'
import { PageHeader } from '@/components/PageHeader'
import { CashflowInsightsCard } from './insights-cards/CasflowInsightsCard'
import { ExpensesInsightsCard } from './insights-cards/ExpensesInsightsCard'
import { IncomeGoalCard } from './insights-cards/IncomeGoalCard'
import { AdvisoryDashboardCard } from './advisory-launch/AdvisoryDashboardCard'
import { UserImportantDatesCard } from './user-important-dates'
import { UserActionItemsCard } from './user-action-items'
import { PracticeOverviewCard } from './PracticeOverviewCard'
import { SupportCenterCard } from './SupportCenterCard'
import { SummaryActions } from './SummaryActions'
import { WebinarCard } from './WebinarCard'
import { PayrollCard } from './PayrollCard'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { userHasFinancialAdvisoryAccess } from '@/features/financial-advisory/service'

// TODO: replace with a custom loader component skipcq: JS-0099
import { Loader } from 'semantic-ui-react'
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'
import { fetchPayrollProfileIfNeeded } from '@/featuresV1/Payroll/payrollActions'
import { selectShouldShowPayrollUpsellCard } from '@/featuresV1/Payroll/payroll.selectors'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { fetchAllEoyReviewStepsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/TaxChecklist/Shared/ReviewStepsandProgresses/allEoyReviewSteps.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilingForms.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { fetchUserEoyReviewProgress } from '@/featuresV1/Taxes/AnnualTaxes/TaxChecklist/Shared/ReviewStepsandProgresses/userEndOfYearReviewProgress.slice'
import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import { fetchUserYearEndModuleStatuses } from '@/featuresV1/YearEndModuleStatus/yearEndModuleStatus.slice'
import {
  selectShowLateFilingAlert,
  selectShowDashboardTaxSeasonHeroBanner,
} from '@/featuresV1/Taxes/AnnualTaxes/annualTaxFilings.selector'
import UsersnapNewUserExperienceAndExpectations from '@/featuresV1/Taxes/AnnualTaxes/UsersnapNewUserExperience'
import TaxSeasonKickoffEntryCard from '@/featuresV1/Taxes/AnnualTaxes/TaxSeasonKickoff/TaxSeasonKickoffEntryCard'
import TaxConsultationCSATSurvey from '@/featuresV1/Taxes/Advisory/TaxConsultationSurvey'
import UsersnapTaxSeasonNPSSurvey from '@/featuresV1/Taxes/AnnualTaxes/UsersnapTaxSeasonNPSSurvey'
import { LateFilingAlert } from '@/featuresV1/Taxes/AnnualTaxes/components/LateFilingAlert'

export const MainDashboard = () => {
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const [ctaWidget, setCtaWidget] = useState('')
  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const shouldDisplayPayrollUpsellCard = useReselector(
    selectShouldShowPayrollUpsellCard
  )

  const { value: showCtaKey } = useFlag(
    FEATURE_FLAG_KEYS.showFinancialInsightsCta,
    'none'
  )

  const { value: enablePracticeOverviewDashboard } = useFlag(
    FEATURE_FLAG_KEYS.enablePracticeOverviewDashboard,
    false
  )

  const { value: enableTaxDeductionsDashboard } = useFlag(
    FEATURE_FLAG_KEYS.enableTaxDeductionsDashboard,
    false
  )

  useEffect(() => {
    if (showCtaKey === 'expense-change') {
      setCtaWidget('expense-change')
    }

    if (showCtaKey === 'income-goal') {
      setCtaWidget('income-goal')
    }

    if (showCtaKey === 'cashflow_balance') {
      setCtaWidget('cashflow_balance')
    }
  }, [showCtaKey])

  const shouldDisplayAdvisoryBetaLaunch = userHasFinancialAdvisoryAccess()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const showLateFilingAlert = useReselector(selectShowLateFilingAlert)

  const shouldShowDashboardTaxSeasonHeroBanner = useReselector(
    selectShowDashboardTaxSeasonHeroBanner
  )

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(fetchSCorpElectionStatus(currentUser.id))
    }
  }, [dispatch, currentUser?.id])

  useEffect(() => {
    async function batchFetch() {
      await Promise.all([
        dispatch(fetchFinancialProfileActionItems()),
        dispatch(fetchUserTransactions(true)), // This should be remove once we migrate transaction related insights to the api
        dispatch(fetchTransactionCategoriesIfNeeded()),
        dispatch(fetchFinancialAccountsIfNeeded()),
        dispatch(fetchAllocationRulesIfNeeded()),
        dispatch(fetchUserDocuments()),
        dispatch(fetchUserDocumentCategoriesIfNeeded()),
        dispatch(
          fetchUserActionItemsIfNeeded({
            forceFetch: true,
          })
        ),
        dispatch(fetchPayrollProfileIfNeeded()),
        //remove the below fetches if LateFilingAlert is ever removed
        dispatch(fetchAnnualTaxFilingFormsIfNeeded()),
        dispatch(fetchAnnualTaxFilingsIfNeeded()),
        dispatch(fetchAllAnnualTaxDetailsIfNeeded()),
        dispatch(fetchAllEoyReviewStepsIfNeeded()),
        dispatch(fetchSubscriptions()),
        dispatch(fetchUserYearEndModuleStatuses()),
      ])

      setFetching(false)
    }

    batchFetch()
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchUserEoyReviewProgress(taxYear))
  }, [dispatch, taxYear])

  return fetching ? (
    <Loader active />
  ) : (
    <>
      {showLateFilingAlert && <LateFilingAlert />}
      {shouldShowDashboardTaxSeasonHeroBanner && <TaxSeasonKickoffEntryCard />}
      {enableTaxDeductionsDashboard && <NewOnHomepageBanner />}

      {!isMobile && !shouldShowDashboardTaxSeasonHeroBanner && (
        <PageHeader header={`Welcome back, ${currentUser?.firstName}.`} />
      )}

      <div className="flex flex-col space-y-4">
        {enablePracticeOverviewDashboard && (
          <div className="space-y-4">
            <SummaryActions />
            <PracticeOverviewCard />
            {enableTaxDeductionsDashboard && <MaximizeTaxDeductionsCard />}
          </div>
        )}

        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-full md:w-11/16">
            <UserActionItemsCard />
          </div>

          <div className="w-full md:w-5/16 space-y-4">
            {/*There is an income goal card in the practice overview so it shouldn't display here if that's enabled*/}
            {ctaWidget === 'income-goal' &&
              !enablePracticeOverviewDashboard && <IncomeGoalCard />}
            {shouldDisplayAdvisoryBetaLaunch && <AdvisoryDashboardCard />}
            {!shouldDisplayAdvisoryBetaLaunch &&
              ctaWidget === 'cashflow_balance' && <CashflowInsightsCard />}
            {!shouldDisplayAdvisoryBetaLaunch &&
              ctaWidget === 'expense-change' && <ExpensesInsightsCard />}
            {!shouldDisplayAdvisoryBetaLaunch &&
              shouldDisplayPayrollUpsellCard && <PayrollCard />}
            <UserImportantDatesCard />
            <WebinarCard />
            <SupportCenterCard />
          </div>
        </div>

        <UsersnapTaxSeasonNPSSurvey />
        <UsersnapNewUserExperienceAndExpectations />
        <TaxConsultationCSATSurvey />
      </div>
    </>
  )
}
